import { useEffect, useState } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.jsx";
import { useSetupState } from "./lib/setup.js";

import SetupPage from "./pages/SetupPage.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import AppShell from "./components/AppShell.jsx";
import DevicesPage from "./pages/DevicesPage.jsx";
import BillingPage from "./pages/BillingPage.jsx";
import AdminUsersPage from "./pages/AdminUsersPage.jsx";
import AdminDevicesPage from "./pages/AdminDevicesPage.jsx";
import AdminLogsPage from "./pages/AdminLogsPage.jsx";
import AdminPlansPage from "./pages/AdminPlansPage.jsx";
import AdminSettingsPage from "./pages/AdminSettingsPage.jsx";
import AdminAuditPage from "./pages/AdminAuditPage.jsx";
import AdminSubscriptionsPage from "./pages/AdminSubscriptionsPage.jsx";
import AdminReleasesPage from "./pages/AdminReleasesPage.jsx";
import AdminTemplatesPage from "./pages/AdminTemplatesPage.jsx";
import BrowserConnectPage from "./pages/BrowserConnectPage.jsx";
import GuestConnectPage from "./pages/GuestConnectPage.jsx";
import AdminBrowserSessionsPage from "./pages/AdminBrowserSessionsPage.jsx";
import DownloadsPage from "./pages/DownloadsPage.jsx";
import DeviceCodePage from "./pages/DeviceCodePage.jsx";
import SsoAuthorizePage from "./pages/SsoAuthorizePage.jsx";
import VorksReturnPage from "./pages/VorksReturnPage.jsx";

/* ─────────────────────────────────────────────────────────────────
   Top-level: check setup state first. If not done, show wizard.
   Once done, show normal auth flow.
   ───────────────────────────────────────────────────────────────── */

export default function App() {
  const { state, error, refresh } = useSetupState();
  const [setupDone, setSetupDone] = useState(null);

  useEffect(() => {
    if (!state) return;
    setSetupDone(state.done === true);

    // While setup is in progress, poll for state changes so DB-reachability
    // and migration-applied transitions auto-advance the wizard.
    if (!state.done) {
      const id = setInterval(refresh, 3000);
      return () => clearInterval(id);
    }
  }, [state, refresh]);

  if (error) {
    return (
      <div className="loading" style={{ flexDirection: "column", gap: 16 }}>
        <span>Backend unreachable</span>
        <span className="mono dim" style={{ fontSize: 11 }}>{error.message}</span>
      </div>
    );
  }

  if (state === null) {
    return <div className="loading">Initializing</div>;
  }

  if (!setupDone) {
    return (
      <SetupPage
        state={state}
        onComplete={() => {
          setSetupDone(true);
          refresh();
        }}
      />
    );
  }

  return (
    <AuthProvider>
      <AuthedRoutes />
    </AuthProvider>
  );
}

function AuthedRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Verifying session</div>;

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/devices" replace /> : <LoginPage />}
      />
      <Route path="/vorks-return" element={<VorksReturnPage />} />

      <Route element={<RequireAuth />}>
        {/* SSO authorize page - rendered standalone, no app shell */}
        <Route path="/sso/:code" element={<SsoAuthorizePage />} />
        {/* Browser-Connect viewer - standalone fullscreen */}
        <Route path="/connect/:token/:deviceId" element={<BrowserConnectPage />} />
        <Route path="/guest/:token" element={<GuestConnectPage />} />

        <Route element={<AppShell />}>
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/device-code" element={<DeviceCodePage />} />

          <Route element={<RequireAdmin />}>
            <Route path="/admin/devices" element={<AdminDevicesPage />} />
            <Route path="/admin/logs" element={<AdminLogsPage />} />
            <Route path="/admin/plans" element={<AdminPlansPage />} />
            <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="/admin/releases" element={<AdminReleasesPage />} />
            <Route path="/admin/templates" element={<AdminTemplatesPage />} />
            <Route path="/admin/browser-sessions" element={<AdminBrowserSessionsPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
          </Route>

          <Route element={<RequireModerator />}>
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/audit" element={<AdminAuditPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/devices" replace />} />
      <Route path="*" element={<Navigate to="/devices" replace />} />
    </Routes>
  );
}

function RequireAuth() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

// Role hierarchy — higher value = more power
const ROLE_RANK = { user: 0, moderator: 1, admin: 2, superadmin: 3 };

function hasRank(userRole, minRole) {
  const have = ROLE_RANK[userRole] ?? -1;
  const need = ROLE_RANK[minRole] ?? 99;
  return have >= need;
}

function RequireAdmin() {
  const { user } = useAuth();
  if (!hasRank(user?.role, "admin")) {
    return <Navigate to="/devices" replace />;
  }
  return <Outlet />;
}

function RequireModerator() {
  const { user } = useAuth();
  if (!hasRank(user?.role, "moderator")) {
    return <Navigate to="/devices" replace />;
  }
  return <Outlet />;
}
